/**
 * React bindings for the live review stream.
 *
 *   · `useWorkspaceStream(slug, onEvent)` — the low-level hook: opens ONE live
 *     subscription for a workspace and calls `onEvent` for every typed frame.
 *     `onEvent` is held in a ref, so a re-render with a fresh closure does NOT
 *     tear down and re-open the socket — only a change of `slug` does.
 *   · `useLiveComments(slug, docId, initial)` — the drop-in for the review rail
 *     / DocumentViewer: seeds from the fetched comment list and live-reconciles
 *     comment-created / reply / status-change events scoped to the open doc.
 *
 * Graceful reconnect is native to `EventSource` (see `stream.ts`); this hook
 * just surfaces the connection `status` for an unobtrusive "reconnecting…" hint.
 */

import { useEffect, useRef, useState } from "react";
import type { Annotation } from "../api/types";
import type { StreamEvent } from "./events";
import { reduceCommentsForDoc } from "./reconcile";
import {
  type EventSourceFactory,
  type StreamStatus,
  subscribeWorkspaceStream,
} from "./stream";

export interface UseWorkspaceStreamOptions {
  /** Injectable EventSource factory (tests pass a fake). */
  factory?: EventSourceFactory;
  /** When false the hook stays closed (e.g. before a workspace is chosen). */
  enabled?: boolean;
}

/** Subscribe to a workspace's live review stream; returns the connection status. */
export function useWorkspaceStream(
  slug: string | null | undefined,
  onEvent: (event: StreamEvent) => void,
  options: UseWorkspaceStreamOptions = {},
): StreamStatus {
  const { factory, enabled = true } = options;
  const [status, setStatus] = useState<StreamStatus>("closed");

  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!slug || !enabled) {
      setStatus("closed");
      return;
    }
    const sub = subscribeWorkspaceStream(
      slug,
      {
        onEvent: (event) => onEventRef.current(event),
        onStatus: setStatus,
      },
      factory,
    );
    return () => sub.close();
  }, [slug, enabled, factory]);

  return status;
}

export interface UseLiveCommentsResult {
  comments: Annotation[];
  status: StreamStatus;
}

/**
 * Live-updating comment list for a single open document. Seeds from `initial`
 * (re-seeds when its identity changes — a fresh fetch or a doc switch) and folds
 * in comment-created / reply / status-change events for `docId`. The caller
 * re-anchors the returned `comments` against the DOM exactly as it does today.
 */
export function useLiveComments(
  slug: string | null | undefined,
  docId: number | null | undefined,
  initial: Annotation[],
  options: UseWorkspaceStreamOptions = {},
): UseLiveCommentsResult {
  const [comments, setComments] = useState<Annotation[]>(initial);

  useEffect(() => {
    setComments(initial);
  }, [initial]);

  const status = useWorkspaceStream(
    slug,
    (event) => {
      if (docId == null) return;
      setComments((prev) => reduceCommentsForDoc(prev, docId, event));
    },
    options,
  );

  return { comments, status };
}
